import { observer } from 'mobx-react-lite'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

type EditProjectDatesProps = {
  field: 'start' | 'end'
  startDate: string
  endDate: string
  onSave: (startDate: string, endDate: string) => void
}

export const EditProjectDates = observer(({
  field,
  startDate,
  endDate,
  onSave,
}: EditProjectDatesProps) => {
  const { translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder

  const [isEditing, setIsEditing] = useState(false)
  const [editedDate, setEditedDate] = useState('')

  const handleStartEdit = () => {
    setEditedDate(field === 'start' ? startDate.slice(0, 10) : endDate.slice(0, 10))
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedDate('')
  }

  const handleSave = async () => {
    const nextStartDate = field === 'start' ? editedDate : startDate.slice(0, 10)
    const nextEndDate = field === 'end' ? editedDate : endDate.slice(0, 10)

    onSave(nextStartDate, nextEndDate)
    setIsEditing(false)
  }

  return (
    <AppBox sx={styles.container}>
      {!isEditing && (
        <AppBox sx={styles.editIconWrapper} onClick={handleStartEdit}>
          <EditIcon sx={styles.editIcon}/>
        </AppBox>
      )}
      {isEditing && (
        <AppBox sx={styles.editContainer}>
          <AppTextField
            type='date'
            value={editedDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditedDate(e.target.value)}
            fullWidth
          />

          <AppBox sx={styles.buttonRow}>
            <AppButton
              fontSize={12}
              variant='outlined'
              onClick={handleCancel}
              textValue={projectBuilderTranslation.cancel()}
            />

            <AppButton
              fontSize={12}
              variant='contained'
              onClick={handleSave}
              textValue={projectBuilderTranslation.save()}
            />
          </AppBox>
        </AppBox>
      )}
    </AppBox>
  )
})
  
const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: 'fit-content',
  },
  editIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    cursor: 'pointer',
    borderRadius: '50%',
    border: '1px solid',
    borderColor: 'secondary.main',
    backgroundColor: 'background.dark',
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'scale(1.05)',
      color: 'secondary.main',
    },
  },
  editIcon: {
    fontSize: 14,
    color: 'inherit',
  },
  editContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
} as const